import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Spin, Typography } from 'antd'
import { useAuthStore } from '@/store/auth'
import { useCapabilitiesStore } from '@/store/capabilities'
import { useImpersonationStore } from '@/store/impersonation'

const { Text } = Typography

/**
 * OIDC callback page.
 *
 * The backend redirects here after successful Zitadel authentication:
 *   /auth/callback?token=...&refresh_token=...
 *
 * This page:
 * 1. Reads tokens from URL search params
 * 2. Stores them in Zustand (persisted to localStorage)
 * 3. Fetches user profile from /auth/me
 * 4. Redirects to the dashboard
 */
function CallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setTokens = useAuthStore((state) => state.setTokens)
  const fetchUser = useAuthStore((state) => state.fetchUser)
  const processed = useRef(false)

  useEffect(() => {
    // Prevent double-processing in React StrictMode
    if (processed.current) return
    processed.current = true

    const token = searchParams.get('token')
    const refreshToken = searchParams.get('refresh_token')

    if (!token || !refreshToken) {
      navigate('/login?error=callback_failed', { replace: true })
      return
    }

    queryClient.clear()
    useCapabilitiesStore.getState().resetCapabilities()
    useImpersonationStore.setState({ impersonatedOrgId: null, impersonatedOrgName: null })

    // Store tokens and fetch user profile
    setTokens(token, refreshToken)

    fetchUser()
      .then(() => {
        navigate('/', { replace: true })
      })
      .catch(() => {
        navigate('/login?error=callback_failed', { replace: true })
      })
  }, [searchParams, navigate, setTokens, fetchUser, queryClient])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        gap: 16,
      }}
    >
      <Spin size="large" />
      <Text type="secondary">Выполняется вход...</Text>
    </div>
  )
}

export default CallbackPage
